import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { listManageableClusters, listSecurity, createAccount, type AccountSummary } from '../../lib/accounts-api';
import type { ManageableClustersResponse } from '@livinhome/shared';
import { extractApiError } from '../../lib/api-client';
import { CredentialsDialog } from '../platform/CredentialsDialog';
import { AccountRowActions, CreateAccountDialogShell } from './AccountDialogs';
import { useT } from '../../i18n';

type Dev = ManageableClustersResponse['developers'][number];

export function SecurityPage() {
  const t = useT();
  const [devs, setDevs] = useState<Dev[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);
  const [items, setItems] = useState<AccountSummary[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [credits, setCredits] = useState<{ email: string; password: string } | null>(null);

  async function selectCluster(cl: { id: string; name: string }) {
    setSelected(cl); setItemsLoading(true);
    try { setItems(await listSecurity(cl.id)); } catch (e) { setError(extractApiError(e).message); } finally { setItemsLoading(false); }
  }
  async function refreshItems() { if (selected) await selectCluster(selected); }

  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const ds = await listManageableClusters();
        setDevs(ds);
        const all = ds.flatMap((d) => d.clusters);
        if (all.length === 1) await selectCluster({ id: all[0].id, name: all[0].name });
      } catch (e) { setError(extractApiError(e).message); } finally { setLoading(false); }
    })();
  }, []);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={600}>{t('sec.title')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('sec.subtitle')}</Typography>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : devs.length === 0 ? (
        <Card variant="outlined"><CardContent><Typography color="text.secondary">{t('cadmin.noClusters')}</Typography></CardContent></Card>
      ) : (
        devs.map((d) => (
          <Card key={d.id} variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: d.clusters.length ? 1 : 0 }}>
                <Typography variant="h6">{d.name}</Typography>
                <Chip size="small" label={t('invite.clustersChip', { count: d.clusters.length, plural: d.clusters.length === 1 ? '' : 's' })} />
              </Stack>
              {d.clusters.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('cadmin.noClusterYet')}</Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {d.clusters.map((cl) => (
                    <Button key={cl.id} size="small" variant={selected?.id === cl.id ? 'contained' : 'outlined'} onClick={() => selectCluster({ id: cl.id, name: cl.name })}>
                      {cl.name}
                    </Button>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {selected ? (
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">{t('sec.sectionTitle', { name: selected.name })}</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>{t('sec.createBtn')}</Button>
            </Stack>
            {itemsLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            ) : items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('sec.empty')}</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>{t('common.colName')}</TableCell><TableCell>{t('common.email')}</TableCell><TableCell>{t('common.status')}</TableCell><TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {items.map((a) => (
                      <TableRow key={a.id} hover>
                        <TableCell>{a.full_name}</TableCell>
                        <TableCell>{a.email}</TableCell>
                        <TableCell>{a.status}</TableCell>
                        <TableCell align="right"><AccountRowActions account={a} onChanged={refreshItems} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      ) : null}

      <CreateSecurityDialog
        open={creating}
        clusterName={selected?.name ?? ''}
        clusterId={selected?.id ?? ''}
        onClose={() => setCreating(false)}
        onCreated={(email, password) => { setCreating(false); setCredits({ email, password }); refreshItems(); }}
      />
      <CredentialsDialog credits={credits} onClose={() => setCredits(null)} />
    </Stack>
  );
}

function CreateSecurityDialog({ open, clusterId, clusterName, onClose, onCreated }: {
  open: boolean; clusterId: string; clusterName: string; onClose: () => void; onCreated: (email: string, password: string) => void;
}) {
  const t = useT();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setFullName(''); setEmail(''); setError(null); } }, [open]);
  async function save() {
    setBusy(true); setError(null);
    try {
      const res = await createAccount({ type: 'security', target_id: clusterId, full_name: fullName.trim(), email: email.trim() });
      onCreated(res.user.email, res.temp_password);
    } catch (e) { setError(extractApiError(e).message); } finally { setBusy(false); }
  }
  return (
    <CreateAccountDialogShell title={t('sec.dialogTitle', { cluster: clusterName })} open={open} onClose={onClose} busy={busy} canSave={Boolean(fullName.trim() && email.trim())} onSave={save} error={error}>
      <TextField label={t('common.fullName')} value={fullName} onChange={(e) => setFullName(e.target.value)} required fullWidth />
      <TextField label={t('common.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth />
    </CreateAccountDialogShell>
  );
}
